import { Suspense, useEffect, useRef } from "react";
import { Canvas, useLoader, useThree } from "@react-three/fiber";
import {
  PerspectiveCamera,
  useGLTF,
  ContactShadows,
  Environment,
} from "@react-three/drei";
import { Color, TextureLoader } from "three";
import { CameraControls } from "./CameraControls";
import { BgColors, Devices } from "../constants";
import { getPublicPath } from "../helpers";
import * as THREE from "three";

const { WHITE } = BgColors;
const { IPHONE_10 } = Devices;

// Model + color
export default function Model({ modelColor = WHITE, device = IPHONE_10 }) {
  const group = useRef();
  const { nodes, materials } = useGLTF(`${getPublicPath(device)}.glb`);

  return (
    <group ref={group} {...device} dispose={null}>
      <group rotation={[-Math.PI, 0, 0]} scale={[-0.0518, -0.0518, -0.0518]}>
        <mesh
          geometry={nodes.Plane001.geometry}
          material={materials.Main}
          material-roughness={0.3}
          material-metalness={0.7}
          material-color={modelColor}
        />
        <mesh
          geometry={nodes.Plane001_1.geometry}
          material={materials.Border}
          material-roughness={0.3}
          material-metalness={0.7}
          material-color={modelColor}
        />
      </group>
    </group>
  );
}

// Screen
const Screen = ({ image = null }) => {
  const texture = useLoader(TextureLoader, image);
  const { gl } = useThree();
  useEffect(() => {
    if (texture) {
      texture.anisotropy = gl.capabilities.getMaxAnisotropy();
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.y = 0.465; // scale texture to match screen
      const xOffset = 0; // offset by X texture
      const yOffset = 0.5; // offset by Y texture
      texture.offset.set(xOffset, yOffset);
    }
  }, [gl, texture]);

    // Custom screen shape with curve > const curveSegments =  32; Need to add to args
    const shapeScreen = new THREE.Shape();
    shapeScreen.moveTo(0, -1.05);
    // heartShape.bezierCurveTo(0, -1.075, 0.1, -1.075, 0.1, -1.075 ); // Left bottom corner curve
    shapeScreen.lineTo(0.03, -1.075);
    shapeScreen.lineTo(0.97, -1.075);
    shapeScreen.lineTo(1, -1.05);
    shapeScreen.lineTo(1, 1.05);
    shapeScreen.lineTo(0.97, 1.075);
    shapeScreen.lineTo(0.03, 1.075);
    shapeScreen.lineTo(0, 1.05);
  
  return (
 <mesh scale={0.0675} position={[-0.034, 0, -0.00001]} rotation={[0, 0, 0]} >
  <shapeBufferGeometry attach="geometry" args={[shapeScreen]} envMap={true}/>
  <meshPhongMaterial attach="material" map={texture} toneMapped={false} />
 </mesh>
  )
};  


// Scene setup
const Scene = ({ background = WHITE, device = IPHONE_10, image = null }) => {
  const sceneRef = useRef(null);
  useThree(({ gl, scene }) => {
    sceneRef.current = scene;
    gl.setPixelRatio(window.devicePixelRatio || 1);
  });

  useEffect(() => {
    if (sceneRef.current) {
      sceneRef.current.background = new Color(background);
    }
  }, [background]);

  return (
    <>
      <PerspectiveCamera
        fov={20}
        near={0.01}
        far={10}
        makeDefault
        position={[0, 0.015, 0.55]}
      >
        <ambientLight intensity={1} />
        <directionalLight position={[20, 20, 20]} intensity={0.4} />
        <pointLight position={[40, 4, 0]} intensity={0.1} />
        <pointLight position={[-5, 10, 0]} intensity={0.5} />
        <pointLight position={[-100, -20, -60]} intensity={0.2} />
      </PerspectiveCamera>

      <CameraControls />
      <Suspense fallback={null}>
        <Environment preset="warehouse" />
        <group position={[0, 0.07, 0]} rotation={[0, 0.75, 0]}>
          <Screen image={image} />
          <Model modelColor={background} />
        </group>
        <ContactShadows
          rotation-x={Math.PI / 2}
          position={[0, -0.0062, 0]}
          opacity={0.4}
          width={0.3}
          height={0.3}
          blur={0.5}
          far={0.01}
        />
      </Suspense>
    </>
  );
};

export const Container = (props) => (
  <Canvas>
    <Scene {...props} />
  </Canvas>
);

export { Container as Scene };
